import { type FC, useCallback, useMemo, useState } from "react";
import { FaRegTrashAlt } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";

import Button from "../../../../../../components/button/button";
import Icon from "../../../../../../components/icon/icon";
import Input from "../../../../../../components/input/input";
import { pollRequest } from "../../../../../../redux/broadcast/broadcast.actions";
import { selectBroadcastId } from "../../../../../../redux/broadcast/broadcast.selectors";
import * as CSS from "./create-poll.styles";

type CreatePollProps = {
	onCancel: () => void;
};

type PollOption = {
	id: string;
	label: string;
};

const MAX_OPTIONS = 5;

const CreatePoll: FC<CreatePollProps> = ({ onCancel }) => {
	const dispatch = useDispatch();
	const broadcastId = useSelector(selectBroadcastId);

	const [question, setQuestion] = useState("");
	const [options, setOptions] = useState<PollOption[]>([
		{ id: "option-1", label: "" },
		{ id: "option-2", label: "" },
	]);

	const handleAddOption = useCallback(() => {
		setOptions((current) => {
			if (current.length >= MAX_OPTIONS) return current;
			return [...current, { id: `option-${current.length + 1}`, label: "" }];
		});
	}, []);

	const handleOptionChange = useCallback((index: number, value: string) => {
		setOptions((current) =>
			current.map((option, i) =>
				i === index ? { ...option, label: value } : option,
			),
		);
	}, []);

	const handleRemoveOption = useCallback((index: number) => {
		setOptions((current) =>
			current
				.filter((_, i) => i !== index)
				.map((option, i) => ({ ...option, id: `option-${i + 1}` })),
		);
	}, []);

	const validOptionLabels = useMemo(
		() => options.map((option) => option.label?.trim()).filter(Boolean),
		[options],
	);

	const isReadyToPost = question?.trim() !== "" && validOptionLabels.length >= 2;

	const handlePostPoll = useCallback(() => {
		if (!broadcastId || !isReadyToPost) return;

		dispatch(
			pollRequest({
				broadcastId,
				question: question?.trim(),
				options: validOptionLabels,
			}),
		);
		onCancel();
	}, [
		broadcastId,
		dispatch,
		isReadyToPost,
		onCancel,
		question,
		validOptionLabels,
	]);

	return (
		<CSS.CreatePoll>
			<CSS.Row>
				<CSS.PollLabel>Question</CSS.PollLabel>
				<Input value={question} onChange={(e) => setQuestion(e.target.value)} />
			</CSS.Row>
			<CSS.Row>
				<CSS.PollLabel>Options</CSS.PollLabel>
				{options.map((option, index) => (
					<CSS.OptionRow key={option.id}>
						<CSS.PollInput
							placeholder={`Option ${index + 1}`}
							value={option.label}
							onChange={(e) => handleOptionChange(index, e.target.value)}
						/>
						{index > 1 && (
							<Icon
								as="button"
								icon={FaRegTrashAlt}
								onClick={() => handleRemoveOption(index)}
								size={27}
							/>
						)}
					</CSS.OptionRow>
				))}
				{options.length < MAX_OPTIONS && (
					<CSS.AddOptionButton onClick={handleAddOption}>
						Add option
					</CSS.AddOptionButton>
				)}
			</CSS.Row>
			<CSS.Row>
				<CSS.ButtonContainer>
					<Button
						onClick={onCancel}
						$variant="secondary"
						style={{ width: "100%" }}
					>
						Cancel
					</Button>
					<Button
						onClick={handlePostPoll}
						disabled={!isReadyToPost}
						style={{ width: "100%" }}
					>
						Post
					</Button>
				</CSS.ButtonContainer>
			</CSS.Row>
		</CSS.CreatePoll>
	);
};

export default CreatePoll;
